import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { BodyMeasurements, User } from "../..";
import { updateUserBodyMeasurements, updateUserProfile } from "../../state/user/userSlice";
import { setTargetValues } from "../../state/meals/mealSlice";
import { useTranslation } from "react-i18next";
import { calculateNutritionTargets, MEALTIME_SPLIT } from "../../common/nutritionTargets";
import { uiTokens } from "../../common/uiTokens"

const activityMarks = (t: (key: string) => string) => [
  { value: 1.2, label: t('onboarding.activity_level.SEDENTARY') },
  { value: 1.55, label: t('onboarding.activity_level.MODERATELY_ACTIVE') },
  { value: 1.9, label: t('onboarding.activity_level.VERY_ACTIVE') }
]

type FormState = {
  weight: string
  height: string
  age: string
  activityLevel: number
  targetWeight: string
  targetDate: string
  measurementDate: string
  chest: string
  waist: string
  hips: string
  buttocks: string
  thigh: string
  calf: string
  plannedChest: string
  plannedWaist: string
  plannedHips: string
  plannedButtocks: string
  plannedThigh: string
  plannedCalf: string
}

type PaceResult = {
  weeklyRate: number
  totalWeeks: number
  isLoss: boolean
  isUnhealthy: boolean
  suggestedDate: string
}

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : Number(trimmed)
}

const calculateBmi = (weightKg?: number, heightCm?: number): number | undefined => {
  if (!weightKg || !heightCm) return undefined
  const heightM = heightCm / 100
  if (heightM <= 0) return undefined
  return weightKg / (heightM * heightM)
}

const formatNumber = (value: number | undefined, precision = 1): string => {
  if (value === undefined || Number.isNaN(value)) {
    return '--'
  }
  return value.toFixed(precision)
}

const safeDiff = (value: number, baseline: number, threshold = 0.5): boolean => {
  return Math.abs(value - baseline) >= threshold
}

const getSuggestedLossDate = (weightDiff: number): string => {
  const safeWeeks = Math.max(1, Math.ceil(Math.abs(weightDiff) / 1))
  const target = new Date(Date.now() + safeWeeks * 7 * 24 * 60 * 60 * 1000)
  return target.toISOString().slice(0, 10)
}

const calculatePace = (weightKg?: number, targetWeightKg?: number, targetDate?: string): PaceResult | undefined => {
  if (!weightKg || !targetWeightKg || !targetDate) {
    return undefined
  }

  const date = new Date(targetDate)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  const now = Date.now()
  const msDiff = date.getTime() - now
  if (msDiff <= 0) {
    return undefined
  }

  const totalWeeks = msDiff / (1000 * 60 * 60 * 24 * 7)
  const weeklyRate = (targetWeightKg - weightKg) / totalWeeks
  const isLoss = weeklyRate < 0
  const isUnhealthy = isLoss && Math.abs(weeklyRate) > 1
  const suggestedDate = getSuggestedLossDate(weightKg - targetWeightKg)

  return {
    weeklyRate,
    totalWeeks,
    isLoss,
    isUnhealthy,
    suggestedDate,
  }
}

export function Bio() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userState = useSelector((state: RootState) => state.user.user)

  const actual = userState.bodyMeasurements?.find((bm) => bm.type === BodyMeasurements.type.ACTUAL)
  const plan = userState.bodyMeasurements?.find((bm) => bm.type === BodyMeasurements.type.PLAN)

  const buildInitial = (): FormState => ({
    weight: actual?.weight?.toString() ?? '',
    height: actual?.height?.toString() ?? '',
    age: userState.age?.toString() ?? '',
    activityLevel: userState.physicalActivityLevel ?? 1.2,
    targetWeight: plan?.weight?.toString() ?? '',
    targetDate: plan?.targetDate ?? '',
    measurementDate: actual?.date ?? '',
    chest: actual?.chest?.toString() ?? '',
    waist: actual?.waist?.toString() ?? '',
    hips: actual?.hips?.toString() ?? '',
    buttocks: actual?.buttocks?.toString() ?? '',
    thigh: actual?.thigh?.toString() ?? '',
    calf: actual?.calf?.toString() ?? '',
    plannedChest: plan?.chest?.toString() ?? '',
    plannedWaist: plan?.waist?.toString() ?? '',
    plannedHips: plan?.hips?.toString() ?? '',
    plannedButtocks: plan?.buttocks?.toString() ?? '',
    plannedThigh: plan?.thigh?.toString() ?? '',
    plannedCalf: plan?.calf?.toString() ?? ''
  })

  const [formValues, setFormValues] = useState<FormState>(buildInitial())
  const [showMealDetails, setShowMealDetails] = useState(false)

  useEffect(() => {
    setFormValues(buildInitial())
  }, [userState.id])

  const parsed = useMemo(() => ({
    weightKg: parseOptionalNumber(formValues.weight),
    heightCm: parseOptionalNumber(formValues.height),
    age: parseOptionalNumber(formValues.age),
    activityLevel: formValues.activityLevel,
    targetWeightKg: parseOptionalNumber(formValues.targetWeight),
    targetDate: formValues.targetDate,
    measurementDate: formValues.measurementDate,
    chest: parseOptionalNumber(formValues.chest),
    waist: parseOptionalNumber(formValues.waist),
    hips: parseOptionalNumber(formValues.hips),
    buttocks: parseOptionalNumber(formValues.buttocks),
    thigh: parseOptionalNumber(formValues.thigh),
    calf: parseOptionalNumber(formValues.calf),
    plannedChest: parseOptionalNumber(formValues.plannedChest),
    plannedWaist: parseOptionalNumber(formValues.plannedWaist),
    plannedHips: parseOptionalNumber(formValues.plannedHips),
    plannedButtocks: parseOptionalNumber(formValues.plannedButtocks),
    plannedThigh: parseOptionalNumber(formValues.plannedThigh),
    plannedCalf: parseOptionalNumber(formValues.plannedCalf)
  }), [formValues])

  const preview = useMemo(() => {
    return calculateNutritionTargets({
      bodyMeasurements: [
        {
          type: BodyMeasurements.type.ACTUAL,
          weight: parsed.weightKg,
          height: parsed.heightCm,
          chest: parsed.chest,
          waist: parsed.waist,
          hips: parsed.hips,
          buttocks: parsed.buttocks,
          thigh: parsed.thigh,
          calf: parsed.calf,
          date: parsed.measurementDate || undefined
        },
        {
          type: BodyMeasurements.type.PLAN,
          weight: parsed.targetWeightKg,
          height: parsed.heightCm,
          targetDate: parsed.targetDate || undefined,
          chest: parsed.plannedChest,
          waist: parsed.plannedWaist,
          hips: parsed.plannedHips,
          buttocks: parsed.plannedButtocks,
          thigh: parsed.plannedThigh,
          calf: parsed.plannedCalf
        }
      ],
      age: parsed.age,
      sex: userState.sex ?? User.sex.MALE,
      physicalActivityLevel: parsed.activityLevel,
    })
  }, [parsed, userState.sex])

  const baseline = useMemo(() => calculateNutritionTargets(userState), [userState])
  const baseHeightCm = actual?.height
  const baseWeightKg = actual?.weight
  const actualBmi = calculateBmi(parsed.weightKg, parsed.heightCm)
  const baseBmi = calculateBmi(baseWeightKg, baseHeightCm)
  const targetBmi = calculateBmi(parsed.targetWeightKg, parsed.heightCm)
  const pace = useMemo(() => calculatePace(parsed.weightKg, parsed.targetWeightKg, parsed.targetDate), [parsed.weightKg, parsed.targetWeightKg, parsed.targetDate])

  const mealDistribution = useMemo(() => {
    const totalEnergy = preview.dailyNutrients.ENERGY || 1

    return Object.keys(MEALTIME_SPLIT).map((mealtime) => {
      const nutrients = preview.mealtimeNutrients[mealtime]
      const calories = nutrients?.ENERGY || 0
      return {
        mealtime,
        calories,
        protein: nutrients?.PROTEIN || 0,
        carbs: nutrients?.CARBOHYDRATE || 0,
        fat: nutrients?.FAT || 0,
        percentage: (calories / totalEnergy) * 100,
        baselineCalories: baseline.mealtimeNutrients[mealtime]?.ENERGY || 0,
      }
    })
  }, [preview.mealtimeNutrients, baseline.mealtimeNutrients])

  const handleSave = () => {
    const actualBmiToSave = calculateBmi(parsed.weightKg, parsed.heightCm)
    const planBmiToSave = calculateBmi(parsed.targetWeightKg, parsed.heightCm)

    dispatch(updateUserBodyMeasurements({
      type: BodyMeasurements.type.ACTUAL,
      weight: parsed.weightKg,
      height: parsed.heightCm,
      bmi: actualBmiToSave,
      chest: parsed.chest,
      waist: parsed.waist,
      hips: parsed.hips,
      buttocks: parsed.buttocks,
      thigh: parsed.thigh,
      calf: parsed.calf,
      date: parsed.measurementDate || undefined
    }))

    dispatch(updateUserBodyMeasurements({
      type: BodyMeasurements.type.PLAN,
      weight: parsed.targetWeightKg,
      height: parsed.heightCm,
      bmi: planBmiToSave,
      targetDate: parsed.targetDate || undefined,
      chest: parsed.plannedChest,
      waist: parsed.plannedWaist,
      hips: parsed.plannedHips,
      buttocks: parsed.plannedButtocks,
      thigh: parsed.plannedThigh,
      calf: parsed.plannedCalf
    }))

    dispatch(updateUserProfile({
      age: parsed.age,
      physicalActivityLevel: parsed.activityLevel,
      dailyNutrients: preview.dailyNutrients,
      mealtimeNutrients: preview.mealtimeNutrients
    }))

    if (Object.keys(preview.mealtimeNutrients).length > 0) {
      dispatch(setTargetValues(preview.mealtimeNutrients))
    }
  }

  const hasEnoughInputs = Boolean(parsed.weightKg && parsed.heightCm && parsed.age && Number.isFinite(parsed.age))
  const currentCaloriesReady = preview.dailyNutrients.ENERGY > 0

  const changedChips = {
    daily: {
      calories: safeDiff(preview.dailyNutrients.ENERGY, baseline.dailyNutrients.ENERGY, 10),
      protein: safeDiff(preview.dailyNutrients.PROTEIN, baseline.dailyNutrients.PROTEIN),
      fat: safeDiff(preview.dailyNutrients.FAT, baseline.dailyNutrients.FAT),
      carbs: safeDiff(preview.dailyNutrients.CARBOHYDRATE, baseline.dailyNutrients.CARBOHYDRATE)
    },
    bmi: safeDiff(actualBmi || 0, baseBmi || 0, 0.1),
    targetBmi: safeDiff(targetBmi || 0, calculateBmi(plan?.weight || 0, baseHeightCm || 0) || 0, 0.1),
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#FBF7F2', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2E2A26' }}>
            {t('bio_settings.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6F675F', mt: 1 }}>
            {t('bio_settings.description')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6E1DB' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Nastavení cílové trajektorie
            </Typography>
            <Typography variant="body2" sx={{ color: '#6F675F', mb: 2 }}>
              Úpravou parametrů doleva okamžitě vidíte, jak se mění cíl.
            </Typography>

            <Stack spacing={2}>
              <TextField
                label={t('onboarding.current_weight')}
                type="number"
                value={formValues.weight}
                onChange={(e) => setFormValues((prev) => ({ ...prev, weight: e.target.value }))}
                fullWidth
              />
              <TextField
                label={t('onboarding.height')}
                type="number"
                value={formValues.height}
                onChange={(e) => setFormValues((prev) => ({ ...prev, height: e.target.value }))}
                fullWidth
              />
              <TextField
                label={t('onboarding.age')}
                type="number"
                value={formValues.age}
                onChange={(e) => setFormValues((prev) => ({ ...prev, age: e.target.value }))}
                fullWidth
              />

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>Detailní míry těla (volitelné)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: '#6F675F', mb: 2 }}>
                    Detailní míry těla nejsou povinné. Umožní lepší sledování změn, ale nejsou nutné pro vygenerování nutričního plánu.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6F675F', mb: 2 }}>
                    Hrudník, pas, boky, zadek, stehna a lýtka můžete zadat pro aktuální i cílový stav.
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    <TextField
                      label="Hrudník"
                      type="number"
                      value={formValues.chest}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, chest: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Hrudník (cíl)"
                      type="number"
                      value={formValues.plannedChest}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, plannedChest: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Pas"
                      type="number"
                      value={formValues.waist}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, waist: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Pas (cíl)"
                      type="number"
                      value={formValues.plannedWaist}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, plannedWaist: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Boky"
                      type="number"
                      value={formValues.hips}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, hips: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Boky (cíl)"
                      type="number"
                      value={formValues.plannedHips}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, plannedHips: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Zadek"
                      type="number"
                      value={formValues.buttocks}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, buttocks: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Zadek (cíl)"
                      type="number"
                      value={formValues.plannedButtocks}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, plannedButtocks: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Stehna"
                      type="number"
                      value={formValues.thigh}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, thigh: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Stehna (cíl)"
                      type="number"
                      value={formValues.plannedThigh}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, plannedThigh: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Lýtka"
                      type="number"
                      value={formValues.calf}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, calf: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Lýtka (cíl)"
                      type="number"
                      value={formValues.plannedCalf}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, plannedCalf: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Datum měření"
                      type="date"
                      value={formValues.measurementDate}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, measurementDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#6F675F' }}>
                  {t('onboarding.activity_level.label')}
                </Typography>
                <Slider
                  value={formValues.activityLevel}
                  onChange={(_e, value) => setFormValues((prev) => ({ ...prev, activityLevel: value as number }))}
                  step={0.175}
                  min={1.2}
                  max={1.9}
                  marks={activityMarks(t)}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {t('bio_settings.goal_section')}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label={t('onboarding.target_weight')}
                type="number"
                value={formValues.targetWeight}
                onChange={(e) => setFormValues((prev) => ({ ...prev, targetWeight: e.target.value }))}
                fullWidth
              />
              <TextField
                label={t('bio_settings.target_date')}
                type="date"
                value={formValues.targetDate}
                onChange={(e) => setFormValues((prev) => ({ ...prev, targetDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ backgroundColor: '#7A4A2E', color: '#FBF7F2', fontWeight: 700, textTransform: 'none' }}
              >
                {t('common_save')}
              </Button>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: '#FFFFFF', border: '1px solid #E6E1DB' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Plánovač cíle (náhled)
            </Typography>

            {!hasEnoughInputs && (
              <Typography variant="body2" sx={{ color: '#6F675F' }}>
                {t('bio_settings.preview_hint')}
              </Typography>
            )}

            {pace?.isUnhealthy && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, backgroundColor: uiTokens.colors.warningBg }}>
                Tempo je pro zdravé hubnutí nadhodnocené ({formatNumber(pace.weeklyRate, 1)} kg/týden). Realističtější cíl je kolem 1,0 kg/týden.
                Doporučený termín dosažení cíle: {pace.suggestedDate}.
              </Alert>
            )}

            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: currentCaloriesReady && changedChips.daily.calories ? uiTokens.colors.adaptiveBg : '#F3EEE8', border: currentCaloriesReady && changedChips.daily.calories ? `1px solid ${uiTokens.colors.adaptiveBorder}` : undefined }}>
                <Typography variant="subtitle2" sx={{ color: '#6F675F' }}>
                  Denní kalorie
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: currentCaloriesReady ? '#2E2A26' : '#9C948C' }}>
                  {currentCaloriesReady ? `${formatNumber(preview.dailyNutrients.ENERGY, 0)} kcal` : '--'}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: changedChips.daily.protein ? uiTokens.colors.adaptiveText : '#6F675F',
                      fontWeight: changedChips.daily.protein ? 700 : 400,
                      backgroundColor: changedChips.daily.protein ? uiTokens.colors.adaptiveBg : 'transparent',
                      px: changedChips.daily.protein ? 1 : 0,
                      borderRadius: changedChips.daily.protein ? 1 : 0
                    }}
                  >
                    {t('common.nutrient.PROTEIN')}: {currentCaloriesReady ? formatNumber(preview.dailyNutrients.PROTEIN, 1) : '--'} g
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: changedChips.daily.carbs ? uiTokens.colors.adaptiveText : '#6F675F',
                      fontWeight: changedChips.daily.carbs ? 700 : 400,
                      backgroundColor: changedChips.daily.carbs ? uiTokens.colors.adaptiveBg : 'transparent',
                      px: changedChips.daily.carbs ? 1 : 0,
                      borderRadius: changedChips.daily.carbs ? 1 : 0
                    }}
                  >
                    {t('common.nutrient.CARBOHYDRATE')}: {currentCaloriesReady ? formatNumber(preview.dailyNutrients.CARBOHYDRATE, 1) : '--'} g
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: changedChips.daily.fat ? uiTokens.colors.adaptiveText : '#6F675F',
                      fontWeight: changedChips.daily.fat ? 700 : 400,
                      backgroundColor: changedChips.daily.fat ? uiTokens.colors.adaptiveBg : 'transparent',
                      px: changedChips.daily.fat ? 1 : 0,
                      borderRadius: changedChips.daily.fat ? 1 : 0
                    }}
                  >
                    {t('common.nutrient.FAT')}: {currentCaloriesReady ? formatNumber(preview.dailyNutrients.FAT, 1) : '--'} g
                  </Typography>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: changedChips.bmi ? uiTokens.colors.adaptiveBg : '#FBF7F2', border: changedChips.bmi ? `1px solid ${uiTokens.colors.adaptiveBorder}` : undefined }}>
                <Typography variant="subtitle2" sx={{ color: '#6F675F', mb: 1 }}>
                  Odhad BMI
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: changedChips.bmi ? uiTokens.colors.adaptiveText : '#2E2A26' }}>
                  {actualBmi ? formatNumber(actualBmi, 1) : '--'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6F675F', mt: 1 }}>
                  Cíl BMI: {targetBmi ? formatNumber(targetBmi, 1) : '--'}
                </Typography>
              </Paper>

              {pace && (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: pace.isUnhealthy ? uiTokens.colors.warningBg : '#F3EEE8' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6F675F', mb: 0.5 }}>
                    Odhad zdravého poklesu/týden
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2E2A26', fontWeight: 700 }}>
                    {pace.isLoss ? `${formatNumber(pace.weeklyRate, 2)} kg/týden` : 'No weight loss target'}
                  </Typography>
                </Paper>
              )}

              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: '#FBF7F2' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#6F675F' }}>
                    Rozložení kalorií do jídel
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowMealDetails((prev) => !prev)}
                    sx={{ minWidth: 0, textTransform: 'none', color: '#7A4A2E' }}
                  >
                    {showMealDetails ? t('client_dashboard.collapse_details') : t('client_dashboard.show_details')}
                  </Button>
                </Box>

                <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                  {mealDistribution.map((meal) => (
                    <Box key={meal.mealtime}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#2E2A26', fontWeight: 600 }}>
                          {t(`common.mealtime.${meal.mealtime}`)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: safeDiff(meal.calories, meal.baselineCalories) ? uiTokens.colors.adaptiveText : '#6F675F',
                            backgroundColor: safeDiff(meal.calories, meal.baselineCalories) ? uiTokens.colors.adaptiveBg : 'transparent',
                            px: safeDiff(meal.calories, meal.baselineCalories) ? 1 : 0,
                            borderRadius: safeDiff(meal.calories, meal.baselineCalories) ? 1 : 0,
                            fontWeight: safeDiff(meal.calories, meal.baselineCalories) ? 700 : 400
                          }}
                        >
                          {formatNumber(meal.calories, 0)} kcal ({formatNumber(meal.percentage, 0)}%)
                        </Typography>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: 999, backgroundColor: '#E8DED3', overflow: 'hidden' }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.max(4, Math.min(100, meal.percentage))}%`,
                            backgroundColor: '#7A4A2E',
                            borderRadius: 999
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Collapse in={showMealDetails}>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    {mealDistribution.map((meal) => (
                      <Paper key={`detail-${meal.mealtime}`} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E6E1DB', backgroundColor: '#FFFFFF' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E2A26' }}>
                          {t(`common.mealtime.${meal.mealtime}`)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6F675F' }}>
                          {t('common.nutrient.PROTEIN')}: {formatNumber(meal.protein, 1)} g · {t('common.nutrient.FAT')}: {formatNumber(meal.fat, 1)} g · {t('common.nutrient.CARBOHYDRATE')}: {formatNumber(meal.carbs, 1)} g
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Collapse>
              </Paper>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
